import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ColorGroupVariant
 * @property {number|string} id
 * @property {boolean} available
 * @property {Record<string, string>} options
 */

/**
 * @typedef {object} ColorGroupProduct
 * @property {string} url
 * @property {ColorGroupVariant[]} variants
 */

/**
 * Keeps PDP cross-product color links aligned with the currently selected specs.
 */
class PdpColorGroup extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const scope = this.closest('.shopify-section') ?? document;

    scope.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectionChange, { signal });
    scope.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantSelectionChange, { signal });
    scope.addEventListener('change', this.#handlePickerChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    this.#updateSwatchLinks();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleVariantSelectionChange = () => {
    this.#updateSwatchLinks();
  };

  /**
   * @param {Event} event
   */
  #handlePickerChange = (event) => {
    if (!(event.target instanceof Element)) return;

    const picker = event.target.closest('variant-picker');
    if (!this.#isCurrentPicker(picker)) return;

    this.#updateSwatchLinks();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[data-color-group-product-id]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    this.#updateSwatchLinks();

    if (link.getAttribute('aria-disabled') !== 'true') return;

    event.preventDefault();
    this.#announceUnavailable(link);
  };

  /**
   * @returns {{ products?: Record<string, ColorGroupProduct> } | undefined}
   */
  #readVariantMap() {
    const script = this.querySelector('script[data-pdp-color-group-variants]');
    if (!script?.textContent) return undefined;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Unable to parse PDP color group variants', error);
      return undefined;
    }
  }

  #updateSwatchLinks() {
    // Read the payload on every update instead of caching it: a Section
    // Rendering morph rewrites the JSON island in place without re-running
    // connectedCallback (this element is not a Component, so it gets no
    // updatedCallback), and a cached snapshot would match specs against
    // pre-morph inventory. The payload is a handful of siblings; parsing it
    // per update is negligible.
    const variantMap = this.#readVariantMap();
    const selections = this.#getCurrentSelections();
    const specSummary = Array.from(selections.values())
      .map((selection) => selection.value)
      .filter(Boolean)
      .join(' / ');
    const unavailableText = this.dataset.unavailableText || 'Unavailable';
    // Distinct wording from unavailableText: a screen reader must hear the
    // difference between "this colour is sold out" and "this colour doesn't
    // come in the specs you selected".
    const soldOutText = this.dataset.soldOutText || 'Sold out';

    for (const link of this.querySelectorAll('a[data-color-group-product-id]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const productId = link.dataset.colorGroupProductId;
      const product = productId ? variantMap?.products?.[productId] : undefined;
      const matchedVariant = product ? this.#findMatchingVariant(product, selections) : undefined;
      // Liquid always renders data-color-name. The title and aria-label are
      // not fallbacks: they hold the previous label, state suffix included,
      // and would compound ("Black, Sold out, Sold out") on every relabel.
      const colorName = link.dataset.colorName || '';

      // Sold out is Liquid-owned: rendered from sibling.available as
      // data-sold-out, spec-invariant, and refreshed by the morph with the
      // rest of the markup. The relabel reads it and never re-derives it from
      // the variant payload, so the attribute and the label cannot disagree.
      const soldOut = link.dataset.soldOut === 'true';
      const soldOutSuffix = soldOut ? `, ${soldOutText}` : '';
      if (product && matchedVariant?.id) {
        link.href = this.#buildVariantUrl(product.url, matchedVariant.id);
        link.dataset.specMatch = 'true';
        link.dataset.matchedVariantId = String(matchedVariant.id);
        link.removeAttribute('aria-disabled');

        const label = (specSummary ? `${colorName}, ${specSummary}` : colorName) + soldOutSuffix;
        link.setAttribute('aria-label', label);
        link.setAttribute('title', label);
      } else {
        const baseUrl = product?.url || link.dataset.baseUrl || link.getAttribute('href') || '#';
        // Spec mismatch and sold out are independent states: a sold-out
        // colour that also lacks the selected specs keeps both words, so a
        // screen reader can tell it from an in-stock colour that merely lacks
        // the combination.
        const label =
          (specSummary ? `${colorName}, ${unavailableText} for ${specSummary}` : `${colorName}, ${unavailableText}`) +
          soldOutSuffix;

        link.href = baseUrl;
        link.dataset.specMatch = 'false';
        link.removeAttribute('data-matched-variant-id');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('aria-label', label);
        link.setAttribute('title', label);
      }
    }
  }

  /**
   * @returns {Map<string, { name: string, value: string }>}
   */
  #getCurrentSelections() {
    const selections = new Map();
    const picker = this.#getVariantPicker();

    if (!picker) return selections;

    for (const input of picker.querySelectorAll('fieldset input:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionName = input.dataset.optionName || '';
      if (!optionName || this.#isColorOption(optionName)) continue;

      selections.set(this.#normalizeOptionName(optionName), {
        name: optionName,
        value: input.value,
      });
    }

    for (const select of picker.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement)) continue;

      const selectedOption = select.selectedOptions[0] || select.querySelector('option[selected]');
      if (!(selectedOption instanceof HTMLOptionElement)) continue;

      const optionName = selectedOption.dataset.optionName || this.#getSelectOptionName(select);
      if (!optionName || this.#isColorOption(optionName)) continue;

      selections.set(this.#normalizeOptionName(optionName), {
        name: optionName,
        value: selectedOption.value,
      });
    }

    return selections;
  }

  /**
   * @returns {Element | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    const scope = this.closest('.shopify-section') ?? document;
    const selector = productId ? `variant-picker[data-product-id="${productId}"]` : 'variant-picker';

    return scope.querySelector(selector) || document.querySelector(selector);
  }

  /**
   * @param {Element | null} picker
   * @returns {boolean}
   */
  #isCurrentPicker(picker) {
    if (!(picker instanceof HTMLElement)) return false;

    const productId = this.dataset.productId;
    return !productId || picker.dataset.productId === productId;
  }

  /**
   * @param {ColorGroupProduct} product
   * @param {Map<string, { name: string, value: string }>} selections
   * @returns {ColorGroupVariant | undefined}
   */
  #findMatchingVariant(product, selections) {
    if (!product.variants?.length) return undefined;

    const selectedOptions = Array.from(selections.values());
    if (!selectedOptions.length) {
      return product.variants.find((variant) => variant.available) || product.variants[0];
    }

    return product.variants.find((variant) =>
      selectedOptions.every((selection) => {
        const variantOptionValue = this.#getVariantOptionValue(variant, selection.name);
        return (
          variantOptionValue !== undefined &&
          this.#normalizeOptionValue(variantOptionValue) === this.#normalizeOptionValue(selection.value)
        );
      })
    );
  }

  /**
   * @param {ColorGroupVariant} variant
   * @param {string} optionName
   * @returns {string | undefined}
   */
  #getVariantOptionValue(variant, optionName) {
    for (const [variantOptionName, variantOptionValue] of Object.entries(variant.options || {})) {
      if (this.#normalizeOptionName(variantOptionName) === this.#normalizeOptionName(optionName)) {
        return variantOptionValue;
      }
    }

    return undefined;
  }

  /**
   * @param {HTMLSelectElement} select
   * @returns {string}
   */
  #getSelectOptionName(select) {
    const match = select.name.match(/^options\[(.*)\]$/);
    if (match?.[1]) return match[1];

    const label = select.closest('.variant-option')?.querySelector('label');
    return label?.textContent?.trim() || '';
  }

  /**
   * @param {string} url
   * @param {number|string} variantId
   * @returns {string}
   */
  #buildVariantUrl(url, variantId) {
    try {
      const targetUrl = new URL(url, window.location.origin);
      targetUrl.searchParams.delete('option_values');
      targetUrl.searchParams.set('variant', String(variantId));
      return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
    } catch (_error) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}variant=${encodeURIComponent(String(variantId))}`;
    }
  }

  /**
   * @param {HTMLAnchorElement} link
   */
  #announceUnavailable(link) {
    const status = this.querySelector('[data-pdp-color-group-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = link.getAttribute('aria-label') || this.dataset.unavailableText || 'Unavailable';
    });
  }

  /**
   * The option names reaching this component come from `data-option-name`,
   * which Liquid renders already translated for the storefront locale — so on
   * a localised market the color option is "Couleur" or "Farbe" and the English
   * literals below do not match it. The theme's own translation of "Color"
   * arrives on `data-color-option-label` and covers that case; both English
   * spellings stay hardcoded because the catalog carries both and one
   * translation key cannot hold a pair.
   *
   * Failing to recognise it is not cosmetic here: an unrecognised color option
   * is treated as a spec to match, so no sibling variant satisfies the
   * selection and every swatch falls back to its unavailable state.
   *
   * @param {string} optionName
   * @returns {boolean}
   */
  #isColorOption(optionName) {
    const normalized = this.#normalizeOptionName(optionName);
    if (normalized === 'color' || normalized === 'colour') return true;

    const localized = this.#normalizeOptionName(this.dataset.colorOptionLabel || '');
    return localized !== '' && normalized === localized;
  }

  /**
   * Canonical form for comparing two option names, which have to survive two
   * different hazards at once:
   *
   * - Sibling products can spell the same spec with different separators
   *   ("Paper Type" vs "Paper-Type"), so separators are removed rather than
   *   preserved, or the two stop matching and every swatch reads unavailable.
   * - It must not filter by character class. Stripping `[^a-z0-9]` reduces a
   *   name in any non-Latin script to an empty string, so "カラー" and "サイズ"
   *   both became "" — they compared equal and collapsed onto one `selections`
   *   key.
   *
   * Removing a fixed separator set satisfies both, and is the one canonical
   * form that Liquid can reproduce exactly (it has no regex). Keep this in
   * step with the `replace` chains in snippets/pdp-color-group-options.liquid
   * and snippets/variant-main-picker.liquid.
   *
   * @param {string} optionName
   * @returns {string}
   */
  #normalizeOptionName(optionName) {
    return optionName.toLowerCase().replace(/[\s\-_]+/g, '');
  }

  /**
   * @param {string} optionValue
   * @returns {string}
   */
  #normalizeOptionValue(optionValue) {
    return String(optionValue).trim();
  }
}

if (!customElements.get('pdp-color-group')) {
  customElements.define('pdp-color-group', PdpColorGroup);
}
